import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoginResponse } from 'angular-auth-oidc-client';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { BrowserNavigation, LoggerProvider } from '@shared';
// eslint-disable-next-line no-restricted-syntax -- Adapter legitimately needs AuthState.clear() for sign-out
import { AuthState, SetAccessTokenUseCase, SetUserUseCase, GetUserUseCase, UserEntity } from '@auth';
import { AppConfigService } from '../../config/app-config.service';
import { ToastProvider } from '../toast/toast.provider';

/**
 * Response shape from GET /api/v1/users/me endpoint.
 * Contains user profile details. Role/permissions are fetched
 * separately via the AuthorizationController (cases/investigations).
 */
interface UserMeResponse {
  userId?: string;
  employerId?: string;
  globalUserId?: string;
  globalEmployerId?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string;
  username?: string;
  isActive?: boolean;
  isSuperAdmin?: boolean;
}

const ACCESS_ERROR_MESSAGES: Record<string, string> = {
  'Employer.NotFound': 'Your organization is not set up in Case Manager. Please contact your administrator.',
  'Employer.Inactive': 'Your organization account is inactive. Please contact support.',
  'User.Inactive': 'Your account is inactive. Please contact your administrator.',
  'User.NotFound': 'Your account was not found in Case Manager. Please contact your administrator.',
};

/**
 * Adapter that bridges EID OIDC authentication data into the application's AuthState.
 * Responsible for mapping LoginResponse to domain entities and executing auth use-cases.
 * After bridging OIDC claims, fetches the full user profile from the API
 * (similar to BackgroundSolutions' loadCurrentUserProfileAsync pattern).
 */
@Injectable({ providedIn: 'root' })
export class EidAuthStateAdapter {
  private readonly _authState = inject(AuthState);
  private readonly _setAccessTokenUseCase = inject(SetAccessTokenUseCase);
  private readonly _setUserUseCase = inject(SetUserUseCase);
  private readonly _getUserUseCase = inject(GetUserUseCase);
  private readonly _http = inject(HttpClient);
  private readonly _configService = inject(AppConfigService);
  private readonly _logger = inject(LoggerProvider).createScoped('EidAuthStateAdapter');
  private readonly _toast = inject(ToastProvider);
  private readonly _browserNavigation = inject(BrowserNavigation);

  private readonly _meAccessResult = signal<boolean | null>(null);

  /**
   * Bridges the EID OIDC session into AuthState so that the auth guard,
   * token interceptor, and all downstream consumers see the user as authenticated.
   * Validates required claims before setting state — aborts if identity is incomplete.
   */
  public bridgeAuthState(response: LoginResponse): void {
    if (!response.accessToken) {
      this._logger.warn('No access token in response, skipping bridge');
      this._authState.clear();
      return;
    }

    const userData = response.userData as Record<string, string> | null;
    const sub = userData?.['sub'];

    if (!sub) {
      this._logger.warn('Missing required "sub" claim, aborting auth state bridge');
      this._authState.clear();
      return;
    }

    try {
      // Set user first to ensure consistency
      const user: UserEntity = {
        id: sub,
        email: userData['email'] || userData['preferred_username'] || '',
        firstName: userData['given_name'] || '',
        lastName: userData['family_name'] || '',
      };
      this._setUserUseCase.execute(user);

      // Then set the access token
      this._setAccessTokenUseCase.execute(response.accessToken);

      this._logger.info('Auth state bridged successfully from EID OIDC response', {
        userId: sub,
        hasAccessToken: !!response.accessToken,
      });

      // Fetch full user profile from API to get complete details
      this.loadCurrentUserProfile();
    } catch (error) {
      this._logger.error('Failed to bridge auth state from EID response', error);
      this._authState.clear();
    }
  }

  /**
   * Fetches the full user profile from the API.
   * This provides complete user details (name, role, etc.) that may not be
   * available in OIDC claims. Matches BackgroundSolutions' loadCurrentUserProfileAsync pattern.
   */
  public loadCurrentUserProfile(): void {
    const apiUrl = this._configService.versionedApiUrl;
    this._http.get<UserMeResponse>(`${apiUrl}/users/me`).subscribe({
      next: (profile) => {
        if (profile) {
          this._logger.info('User profile loaded from API');
          const currentUser = this._getUserUseCase.execute()();
          const updatedUser: UserEntity = {
            id: profile.userId || currentUser?.id || '',
            email: profile.email || currentUser?.email || '',
            firstName: profile.firstName || currentUser?.firstName || '',
            lastName: profile.lastName || currentUser?.lastName || '',
            role: currentUser?.role,
            isSuperAdmin: profile.isSuperAdmin ?? false,
          };
          this._setUserUseCase.execute(updatedUser);
        }
      },
      error: (err) => {
        if (err.status === 403) {
          const code = (err.error as { code?: string } | null)?.code ?? '';
          const message = ACCESS_ERROR_MESSAGES[code] ?? 'Access denied. Please contact your administrator.';
          this._logger.warn('Access denied loading user profile', { code, error: err });
          this._toast.error('Access denied', message);
          const redirectUri = encodeURIComponent(window.location.origin);
          if (!this._configService.isLocal()) {
            this._browserNavigation.navigateToUrl(
              `${this._configService.appSwitcherBaseUrl}/connect/endsession?post_logout_redirect_uri=${redirectUri}`
            );
          } else {
            this._browserNavigation.navigateToUrl(this._configService.loginPath);
          }
        } else {
          this._logger.warn('Failed to load user profile from API, using OIDC claims', { error: err });
        }
      },
    });
  }

  /**
   * Checks whether the current user has a valid tenant by calling GET /users/me.
   * Returns true if the endpoint responds successfully, false on 401 or 404
   * (user exists in the identity provider but has no agency/tenant assigned).
   * Any other error (network, 5xx) is treated as ok to avoid blocking the user.
   *
   * Also updates the user profile in AuthState when successful, so callers
   * do not need a separate loadCurrentUserProfile() call.
   *
   * Result is cached for the lifetime of the session; cleared on sign-out.
   */
  public checkUserAccess(): Observable<boolean> {
    const cached = this._meAccessResult();
    if (cached !== null) {
      return of(cached);
    }

    const apiUrl = this._configService.versionedApiUrl;
    return this._http.get<UserMeResponse>(`${apiUrl}/users/me`).pipe(
      tap((profile) => {
        this._logger.info('User profile loaded via checkUserAccess');
        const currentUser = this._getUserUseCase.execute()();
        const updatedUser: UserEntity = {
          id: profile.userId || currentUser?.id || '',
          email: profile.email || currentUser?.email || '',
          firstName: profile.firstName || currentUser?.firstName || '',
          lastName: profile.lastName || currentUser?.lastName || '',
          role: currentUser?.role,
          isSuperAdmin: profile.isSuperAdmin ?? false,
        };
        this._setUserUseCase.execute(updatedUser);
        this._meAccessResult.set(true);
      }),
      map(() => true),
      catchError((err: { status?: number; error?: unknown }) => {
        if (err.status === 404) {
          this._meAccessResult.set(false);
          return of(false);
        }
        if (err.status === 403) {
          const code = (err.error as { code?: string } | null)?.code ?? '';
          if (code === 'Employer.NotFound') {
            this._meAccessResult.set(false);
            return of(false);
          }

          return of(false);
        }
        if (err.status === 401) {
          return of(false);
        }
        // Network errors, 5xx, etc. — don't block the user.
        this._logger.warn('Could not reach /me, allowing navigation', { status: err.status });
        return of(true);
      }),
    );
  }

  /**
   * Clears the application auth state on sign-out.
   */
  public clearAuthState(): void {
    this._authState.clear();
    this._meAccessResult.set(null);
  }
}
